import React, { useCallback, useState } from 'react';
import { ISourceDataStore } from '@stores/sourceDataStore';
import { inject, observer } from 'mobx-react';
import classes from '@components/sources/source-view/source-schema/sync-schema.module.scss';
import Button from '@ui-library/button';
import ConfigurationEdit from '@components/sources/source-view/source-configure-data/configuration-edit';
import ConfigurationView from '@components/sources/source-view/source-configure-data/configuration-view';
import removeNullValues from '@utils/removeNullValues';
import { IMessageStore } from '@stores/messages';
import sourceFactory from '@components/sources/sourceFactory';
import { SourceConfig } from '@stores/source';

const sourceValidationExclude = ['marketo'];

interface OwnProps {
  sourceDataStore?: ISourceDataStore;
  messagesStore?: IMessageStore;
  isAdmin: boolean;
}

type Props = OwnProps;

const SourceConfigureData = (props: Props) => {
  const { sourceDataStore: source, isAdmin, messagesStore } = props;
  const [config, setConfig] = useState<null | SourceConfig | undefined>(null);
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);

  const handleValidation = async () => {
    try {
      const createSourceData = {
        name: source?.name,
        sourceDefinitionId: source?.sourceDefinitionId,
        config: {},
      };
      if (!config) {
        return undefined;
      }
      const { accountInfo, ...updatedConfig } = config;

      createSourceData.config = removeNullValues(updatedConfig);
      const skipValidation = sourceValidationExclude.includes(source?.sourceDef!.name!);
      let validation;
      if (skipValidation) {
        validation = { valid: true, reason: '' };
      } else {
        validation = await source!.validateSource({
          ...createSourceData.config,
        });
      }
      if (!validation.valid) {
        let { reason } = validation;
        try {
          let jsonReason = JSON.parse(reason);
          reason = jsonReason.response.body;
          jsonReason = JSON.parse(reason);
          reason = jsonReason.error.message;
        } catch (err) {}
        messagesStore!.showErrorMessage(reason);
        return false;
      }
      return true;
    } catch (err) {
      messagesStore!.showErrorMessage('Something went wrong. Please try again later.');
      return false;
    }
  };

  const handleChange = (data: SourceConfig, isValid: boolean) => {
    setConfig({ ...data });
    setIsFormValid(isValid);
  };
  const handleEditClick = useCallback(() => {
    setEdit(true);
    setConfig({});
  }, [setEdit, setConfig]);

  const handleCancelEdit = useCallback(() => {
    setEdit(false);
    setConfig(undefined);
  }, [setConfig, setEdit]);

  const handleSave = async () => {
    try {
      setLoading(true);

      const isValid = await handleValidation();
      if (!isValid) {
        setLoading(false);
        return false;
      }
      if (!config) {
        return undefined;
      }
      const data = sourceFactory(source?.sourceDef!.category).transformSourceConfig(config);
      await source?.updateConfig(data);
      setEdit(false);
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  return (
    <>
      <div className={classes.header}>
        <div className={classes.header_info}>
          <div className={classes.header_info__main_text}>Select configuration</div>
          {edit ? (
            <div className={classes.header_info__sub_text}>Select configuration for the source</div>
          ) : null}
        </div>
        <div className={classes.header_cta}>
          {isAdmin && !edit ? <Button secondary onClick={handleEditClick} name="Update" /> : null}
          {edit ? <Button secondary onClick={handleCancelEdit} name="Cancel" /> : null}
          {edit ? (
            <Button
              disabled={!isFormValid}
              className={classes.header_cta__save}
              onClick={handleSave}
              loading={loading}
              name="Save"
            />
          ) : null}
        </div>
      </div>
      <div
        className={`${classes.configure_data_container} ${
          !edit ? classes.view_mode : ''
        } m-auto fit-width`}
      >
        {edit ? (
          <ConfigurationEdit source={source as ISourceDataStore} onChange={handleChange} />
        ) : (
          <ConfigurationView source={source as ISourceDataStore} />
        )}
      </div>
    </>
  );
};

export default inject('sourceDataStore', 'messagesStore')(observer(SourceConfigureData));
